import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";

import FiltersOnCampaignsTable from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table";
import {
  TableLoader,
  DataGrid,
  TableSearchAndFilters,
  TableContainer,
  TableFillHeightBody,
  TableItemsNotFound,
  shouldPinPrimaryColumnLeft,
} from "ui/data-grid";

import { useGetProgramTypesQuery } from "hooks/api/dictionary";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCampaigns } from "hooks/api/use-campaigns";

import { createCampaignsTableColumns } from "containers/admin/clients/client/campaigns-page/components/campaigns-table/campaigns-table-columns";
import { usePermissionBlacklist } from "hooks/use-permission-blacklist";
import { useUserLayout, useDebouncedUpdateUserLayout } from "hooks/api/use-user-layout";

import { Permission } from "types/auth";
import { FiltersToApplyOnCampaignsPage } from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table.types";
import { ColumnOption } from "types/table";

import {
  DEFAULT_CAMPAIGNS_FILTERS_TO_APPLY,
  FILTER_ID_TO_TITLE,
} from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table.consts";

const TABLE_CONFIG_KEY = "campaigns";
const PRIMARY_COLUMN_IDS = ["name", "actions"];

const CampaignsTable = ({ actionSlot }: { actionSlot?: React.ReactNode }) => {
  const navigate = useNavigate();

  const currentClient = useCurrentClient();
  usePermissionBlacklist([Permission.PERM_CLIENT_USER]);

  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");

  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyOnCampaignsPage>(
    DEFAULT_CAMPAIGNS_FILTERS_TO_APPLY,
  );

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastModifiedDate", desc: true },
  ]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data: programTypes = [] } = useGetProgramTypesQuery();

  const { data: tableColumnsConfig = {} } = useUserLayout();
  const { toggleColumnVisibility } = useDebouncedUpdateUserLayout();

  const handleToggleColumn = (columnId: string) => {
    toggleColumnVisibility(TABLE_CONFIG_KEY, columnId);
  };

  // Wrapper functions that reset page index at the same time as filters/search change
  const updateFilters = (newFilters: FiltersToApplyOnCampaignsPage) => {
    setFiltersToApply(newFilters);
    setPageIndex(0);
  };

  const updateSearch = (term: string) => {
    setCurrentSearchTerm(term);
    setPageIndex(0);
  };

  const sortQueryParam = useMemo(
    () => sortingParams.map((param) => `${param.id},${param.desc ? "desc" : "asc"}`).join(","),
    [sortingParams],
  );

  const { data, isLoading, isFetching } = useCampaigns(
    {
      clientId: currentClient.id,
      page: pageIndex,
      size: pageSize,
      sort: sortQueryParam || undefined,
      search: currentSearchTerm || undefined,
      filters: filtersToApply,
    },
    { placeholderData: keepPreviousData },
  );

  const campaigns = data?.items ?? [];
  const totalRecords = data?.totalItems ?? 0;
  const pagesAmount = data?.totalPages ?? 0;

  const handleAmountChange = ({ pageSize: nextPageSize }: { pageSize: number }) => {
    setPageSize(nextPageSize);
    setPageIndex(0);
  };

  const allColumns = useMemo(
    () =>
      createCampaignsTableColumns({
        pathname: location.pathname,
        programTypes,
      }),
    [location.pathname, navigate],
  );

  const columnOptions = useMemo<ColumnOption[]>(
    () =>
      allColumns
        .filter(
          (col) => typeof col.header === "string" && col.id !== "name" && col.id !== "actions",
        )
        .map((col) => ({ id: col.id!, label: col.header as string })),
    [allColumns],
  );

  const hiddenColumnIds = useMemo(
    () => new Set(tableColumnsConfig[TABLE_CONFIG_KEY]?.hidden ?? []),
    [tableColumnsConfig],
  );

  const columns = useMemo(
    () => allColumns.filter((col) => !hiddenColumnIds.has(col.id ?? "")),
    [allColumns, hiddenColumnIds],
  );

  const pinPrimaryColumnLeft = shouldPinPrimaryColumnLeft(columns, PRIMARY_COLUMN_IDS);

  return (
    <TableContainer fillHeight>
      <TableSearchAndFilters
        setCurrentSearchTerm={updateSearch}
        filtersToApply={filtersToApply}
        setFiltersToApply={updateFilters}
        defaultFiltersToApply={DEFAULT_CAMPAIGNS_FILTERS_TO_APPLY}
        filterIdToTitle={FILTER_ID_TO_TITLE}
        tableFilters={
          <FiltersOnCampaignsTable
            filtersToApply={filtersToApply}
            setFiltersToApply={updateFilters}
            tableColumnsConfig={tableColumnsConfig}
            columnOptions={columnOptions}
            onToggleColumn={handleToggleColumn}
            isFetching={isFetching}
          />
        }
        actionSlot={actionSlot}
      />

      <TableFillHeightBody>
        <TableLoader isLoading={isLoading} fullScreen />

        {campaigns.length > 0 ? (
          <DataGrid
            columns={columns}
            data={campaigns}
            isLoading={isFetching}
            manualSorting={false}
            showingRecordsIsOn={true}
            viewByAmountSelectIsOn={true}
            totalRecords={totalRecords}
            pageSize={pageSize}
            pagesAmount={pagesAmount}
            setAmount={handleAmountChange}
            pageIndex={pageIndex}
            setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            pinPrimaryColumnLeft={pinPrimaryColumnLeft}
            fillHeight
          />
        ) : (
          !isFetching && <TableItemsNotFound />
        )}
      </TableFillHeightBody>
    </TableContainer>
  );
};

export default CampaignsTable;
